import { FC, useMemo, useState } from 'react';
import { PremiumPrice } from 'src/api/internal/schema';
import { PlansScreenTrackingLocation } from 'src/api/tracking/types';
import { useCurrentLanguagePair } from 'src/hooks/useCurrentLanguagePair';
import { useDailyNewWords } from 'src/hooks/useDailyNewWords';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { DEFAULT_WORDS_PER_DAY } from 'src/apps/Onboarding/BlobOnboarding/dailyGoal';
import { StepLoader } from 'src/apps/Onboarding/BlobOnboarding/StepLayout';
import {
  getBlobPlanPrices,
  PlansComponent,
} from 'src/apps/Onboarding/BlobOnboarding/Plans/PlansComponent';
import { useBlobPlansTracking } from 'src/apps/Onboarding/BlobOnboarding/Plans/useBlobPlansTracking';

export const BLOB_PLANS_TRACKING_LOCATION: PlansScreenTrackingLocation = 'plans_screen';

type BlobPlansPageProps = {
  prices: PremiumPrice[];
  /** Checks out with the given plan */
  onSubscribe: (price: PremiumPrice) => void;
  /** Leaves the plans page. Without it there's no way out but checkout. */
  onClose?: () => void;
  /** Where the learner opened the plans page from, for tracking */
  getSource: () => string;
};

/** /payment/plans for blob learners: the onboarding plans step, minus the onboarding. */
export const BlobPlansPage: FC<BlobPlansPageProps> = ({
  prices: allPrices,
  onSubscribe,
  onClose,
  getSource,
}) => {
  const languagePairId = getLanguagePairId();
  const { languagePair } = useCurrentLanguagePair();
  const { data: dailyNewWords, isLoading: isGoalLoading } = useDailyNewWords(languagePairId);
  const wordsPerDay = dailyNewWords?.goal || DEFAULT_WORDS_PER_DAY;

  const prices = useMemo(() => getBlobPlanPrices(allPrices), [allPrices]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // Lifetime first, so it's selected until the learner picks another plan.
  const selectedPrice = prices.find(price => price.key === selectedKey) ?? prices[0];

  const isReady = !!selectedPrice && !isGoalLoading;
  const tracking = useBlobPlansTracking({
    location: BLOB_PLANS_TRACKING_LOCATION,
    getSource,
    isReady,
  });

  if (!isReady) {
    return <StepLoader />;
  }

  const handleSelectPrice = (price: PremiumPrice) => {
    tracking.trackPlanSelected(price);
    setSelectedKey(price.key);
  };

  const handleSubscribe = () => {
    tracking.trackSubscribe(selectedPrice);
    onSubscribe(selectedPrice);
  };

  const handleContinueFree = () => {
    tracking.trackDismiss('continue_for_free');
    onClose?.();
  };

  const handleBack = () => {
    tracking.trackDismiss('back');
    onClose?.();
  };

  return (
    <PlansComponent
      variant="plans"
      prices={prices}
      selectedPrice={selectedPrice}
      wordsPerDay={wordsPerDay}
      language={languagePair?.targetLanguageName}
      onSelectPrice={handleSelectPrice}
      onTabChange={tracking.trackTabChange}
      onSubscribe={handleSubscribe}
      onContinueFree={onClose ? handleContinueFree : undefined}
      onBack={onClose ? handleBack : undefined}
    />
  );
};
